import React from "react";
import styled from "@emotion/styled";
import { filter, find, isArray, isEmpty, map, reduce } from "lodash";
import { createURL, isBrowser } from "~/utilities/helpers";
import { navigate } from "gatsby";

import FaIcon from "~/components/FaIcon";

import { rotateCentered } from "~/styles/keyframes";

const SEPARATOR = "|";

const LoadingIcon = styled(FaIcon)`
    margin-bottom: -1rem;
    animation: ${rotateCentered} 1s linear infinite normal forwards;
`;

const NoMatchesIcon = styled(FaIcon)`
    margin-right: 1rem;
`;

const createArray = (string) => {
    if (!string) {
        return [];
    }

    return string.split(SEPARATOR);
};

const formatPageSize = (paramPageSize) => {
    const validSizes = [25, 50, 100, 150, 200];
    let pageSize = 25;

    if (validSizes.includes(paramPageSize)) {
        pageSize = paramPageSize;
    }
    return pageSize;
};

const parseIntParams = (string, base, paramDefault) => {
    const parsed = parseInt(string, base);
    if (isNaN(parsed)) {
        return paramDefault;
    }
    return parsed;
};

export const getSuggestions = ({ searchQuery, items, isError, loading }) => {
    let suggestions = [];

    if (loading && searchQuery !== "") {
        suggestions = [
            {
                id: "loading",
                title: "Loading",
                icon: <LoadingIcon name="spinner" />,
            },
        ];
    } else if ((isEmpty(items) && searchQuery !== "") || isError) {
        suggestions = [
            {
                id: "no-matches",
                title: "No Matches",
                icon: <NoMatchesIcon name="circle-exclamation" />,
            },
        ];
    } else if (searchQuery !== "") {
        suggestions = items;
    }

    return suggestions;
};

export const getQueryParam = (search, paramName = "") => {
    if (!isBrowser) {
        return "";
    }

    const urlSearchParams = new URLSearchParams(search);

    if (!paramName) {
        return urlSearchParams;
    }

    if (urlSearchParams.has(paramName)) {
        return urlSearchParams.get(paramName) || "";
    }

    return "";
};

const getPageParams = (search) => {
    const initialQuery = getQueryParam(search, "q");

    const initialPageSize = parseIntParams(
        getQueryParam(search, "size"),
        10,
        25,
    );
    const initialPageIndex = parseIntParams(
        getQueryParam(search, "page"),
        10,
        1,
    );
    return {
        initialQuery,
        initialPageIndex: initialPageIndex - 1,
        initialPageSize: formatPageSize(initialPageSize),
    };
};

export const getUrlFromElasticsearch = (elasticsearchDocument, getBasePath) => {
    const sanitizeUrl = (url) => {
        return url
            .replace(process.env.GATSBY_ADMIN_SITE_URL, "")
            .replace("https://cms.$site.test", "");
    };

    const parsePageUrl = (url, post_lang) => {
        const href = sanitizeUrl(url);
        const queryStringIndex = href.indexOf("?");
        const end = queryStringIndex > -1 ? queryStringIndex : href.length;
        const hrefNoQueryString = href.slice(0, end);
        const code = post_lang === "en" ? "" : post_lang;
        return `${hrefNoQueryString}${code}`;
    };

    const parseSlug = (slug, postLang) => {
        if (postLang === "zh-tw") {
            return slug.replace("/cn", "/zh-tw");
        }

        if (postLang === "zh-cn") {
            return slug.replace("/zh-hans", "/zh-cn");
        }

        return slug;
    };

    const PATHS_MAPPINGS = {
        "knowledge-center": "knowledgeCenterArticle",
        news: "newsBase",
        events: "eventsBase",
        al_product: "productsBase",
        page: "pagesBase",
        dlm_download: "documentsBase",
    };

    const {
        post_name: slug,
        permalink,
        post_type: postType,
        post_lang,
        meta,
    } = elasticsearchDocument || {};

    if (postType === "page") {
        const url = createURL(permalink);
        return parsePageUrl(url.href, post_lang);
    }

    const urlOverride =
        find(meta?.url_override, ({ value }) => value)?.value || "";

    if (postType === "knowledge-center" && urlOverride) {
        return urlOverride;
    }

    const parsedSlug = parseSlug(slug, post_lang);
    const baseUrl = getBasePath(PATHS_MAPPINGS[postType])?.url || "";

    return sanitizeUrl(`${baseUrl}${parsedSlug}/`);
};

export const getTypeLabel = (post_type) => {
    switch (post_type) {
        case "dlm_download:vnd.openxmlformats-officedocument.presentationml.slideshow":
            return "Presentations";
        case "dlm_download:pdf":
            return "PDF Files";
        case "dlm_download:zip":
            return "ZIP Archives";
        case "dlm_download:txt":
            return "Text Files";
        case "dlm_download:video":
            return "Videos";
        case "dlm_download:ppsx":
            return "PowerPoint Files";
        case "attachment:pdf":
            return "PDF Files";
        case "attachment:zip":
            return "ZIP Archives";
        case "attachment:txt":
            return "Text Files";
        case "attachment:video":
            return "Videos";
        case "attachment:ppsx":
            return "PowerPoint Files";
        case "al_product":
            return "Products";
        case "page":
            return "Web Pages";
        case "knowledge-center":
            return "Knowledge Center";
        case "news":
            return "News";
        case "events":
            return "Events";
        case "dealers":
            return "Dealers";
        default:
            if (post_type.includes("dlm_download")) {
                const fileType = post_type.split(":")[1];
                if (fileType) {
                    return `${fileType.toUpperCase()} Files`;
                }
                return "Document Library";
            }
            if (post_type.includes("attachment")) {
                const fileType = post_type.split(":")[1];
                if (fileType) {
                    return `${fileType.toUpperCase()} Files`;
                }
                return "Attachments";
            }
            return post_type;
    }
};

export const formatDefaultFilters = (defaultFilters) => {
    return reduce(
        defaultFilters,
        (acc, value, key) => {
            if (key === "productLine") {
                return { ...acc, [key]: value?.nodes?.[0]?.slug || "" };
            }
            return {
                ...acc,
                [key]: value?.nodes ? value.nodes.map(({ slug }) => slug) : [],
            };
        },
        {},
    );
};

export const setIsActive = (categories, productType) => {
    const options = [
        {
            active: true,
            slug: "view-all",
            disabled: false,
            name: "View All",
            id: "view-all",
        },
    ];

    categories?.forEach((categoryOption) => {
        const isActive = productType === categoryOption.slug;
        if (isActive) {
            const viewAllOption = options[0];
            viewAllOption.active = false;
        }
        options.push({ ...categoryOption, active: isActive });
    });

    return options;
};

export const createParams = (filters) => {
    const params = {};

    for (const filter in filters) {
        const value = filters[filter];
        if (typeof value === "number" || !isEmpty(value)) {
            if (isArray(value)) {
                params[filter] = value.join(SEPARATOR);
            } else {
                params[filter] = value;
            }
        }
    }

    return params;
};

export const updateUrl = (params, origin, pathname) => {
    if (isBrowser) {
        const url = createURL(`${origin}${pathname}`);
        for (const param in params) {
            url.searchParams.set(param, params[param]);
        }

        navigate(url.search, {
            state: { disableScrollUpdate: true },
        });
    }
};

export const getUrl = (params, origin, pathname) => {
    if (isBrowser) {
        const url = createURL(`${origin}${pathname}`);
        for (const param in params) {
            url.searchParams.set(param, params[param]);
        }
        return url;
    }

    return null;
};

export const hideFilters = (filters, valuesToHide) => {
    const filtersToHide = map(valuesToHide, "slug");
    return map(filters, (documentFilter) => {
        if (documentFilter.type === "productLine") {
            return {
                ...documentFilter,
                values: filter(documentFilter.values, (productLine) => {
                    return !filtersToHide.includes(productLine.slug);
                }),
            };
        }
        return documentFilter;
    });
};

export const formatSearchParams = (search) => {
    const initialProductLine = getQueryParam(search, "productLine");
    const initialTopics = getQueryParam(search, "topics");
    const initialTypes = getQueryParam(search, "types");
    const initialDocumentType = getQueryParam(search, "documentType");
    const initialLanguages = getQueryParam(search, "languages");
    const pageParams = getPageParams(search);

    return {
        query: pageParams.initialQuery,
        size: pageParams.initialPageSize,
        pageIndex: pageParams.initialPageIndex,
        productLine: initialProductLine,
        topics: createArray(initialTopics),
        documentType: createArray(initialDocumentType),
        types: createArray(initialTypes),
        languages: createArray(initialLanguages),
    };
};

export const formatDocumentSearchParams = (search) => {
    const initialProductLine = getQueryParam(search, "productLine");
    const initialTopics = getQueryParam(search, "topics");
    const initialProductCategory = getQueryParam(search, "productCategory");
    const initialFormat = getQueryParam(search, "format");
    const initialDocumentType = getQueryParam(search, "documentType");
    const sortByTitle = getQueryParam(search, "sortByTitle");
    const sortByType = getQueryParam(search, "sortByType");
    const pageParams = getPageParams(search);

    return {
        query: pageParams.initialQuery,
        size: pageParams.initialPageSize,
        pageIndex: pageParams.initialPageIndex,
        productLine: initialProductLine,
        topics: createArray(initialTopics),
        productCategory: createArray(initialProductCategory),
        format: createArray(initialFormat),
        documentType: createArray(initialDocumentType),
        sortByTitle,
        sortByType,
    };
};
